import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faEdit, 
  faTrashAlt,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { Assistance } from '../../types/Assistance';
import { getAssistance, deleteAssistance } from '../../services/assistanceService';
import './AssistanceDetail.css';

const AssistanceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [assistance, setAssistance] = useState<Assistance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const data = await getAssistance(parseInt(id));
        setAssistance(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching assistance data:', err);
        setError('No se pudo cargar la información de la asistencia. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);
  
  const handleDelete = async () => {
    if (!assistance || !assistance.id) return;
    
    if (window.confirm('¿Está seguro de que desea eliminar esta asistencia? Esta acción no se puede deshacer.')) {
      try {
        await deleteAssistance(assistance.id);
        navigate('/assistances');
      } catch (err) {
        console.error('Error deleting assistance:', err);
        setError('No se pudo eliminar la asistencia. Por favor, intente nuevamente.');
      }
    }
  };
  
  // Formatea la fecha en un formato más legible (DD/MM/YYYY)
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'No registrada';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
  };
  
  if (loading) {
    return <div className="loading-container">Cargando información de la asistencia...</div>;
  }
  
  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }
  
  if (!assistance) {
    return <div className="alert alert-warning">No se encontró la asistencia.</div>;
  }
  
  // Datos del beneficiario
  const beneficiaryData = typeof assistance.beneficiary === 'number' 
    ? { id: assistance.beneficiary, name: `Beneficiario #${assistance.beneficiary}` }
    : { 
        id: assistance.beneficiary.id, 
        name: `${assistance.beneficiary.first_name || ''} ${assistance.beneficiary.last_name || ''}`.trim() || 'Sin nombre'
      };
  
  return (
    <div className="assistance-detail-container">
      <div className="flex justify-between align-center mb-4">
        <div className="flex align-center">
          <button 
            className="btn btn-secondary mr-3"
            onClick={() => navigate('/assistances')}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Volver
          </button>
          <h1>Detalle de Asistencia</h1>
        </div>
        
        <div className="action-buttons">
          <Link 
            to={`/assistances/${assistance.id}/edit`} 
            className="btn btn-primary mr-2"
          >
            <FontAwesomeIcon icon={faEdit} /> Editar
          </Link>
          <button 
            className="btn btn-danger"
            onClick={handleDelete}
          >
            <FontAwesomeIcon icon={faTrashAlt} /> Eliminar
          </button>
        </div>
      </div>
      
      <div className="card mb-4">
        <div className="assistance-header">
          <div className="assistance-title">
            <h2>Asistencia: {assistance.assistance_type || 'Sin tipo específico'}</h2>
            <span className={`status-badge ${assistance.extended_assistance ? 'status-pending' : 'status-delivered'}`}>
              {assistance.extended_assistance ? 'Requiere seguimiento' : 'No requiere seguimiento'}
            </span>
          </div>
          <div className="assistance-beneficiary">
            <Link to={`/beneficiaries/${beneficiaryData.id}`} className="beneficiary-link">
              <FontAwesomeIcon icon={faUser} /> {beneficiaryData.name}
            </Link>
          </div>
        </div>
        
        <div className="assistance-content">
          <div className="section">
            <h3>Información General</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Asistente:</span>
                <span className="info-value">{assistance.author}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Fecha:</span>
                <span className="info-value">{formatDate(assistance.created_at)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Primera asistencia:</span>
                <span className="info-value">{assistance.is_first_assistance ? 'Sí' : 'No'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Seguimiento:</span>
                <span className="info-value">
                  <span className={`status-badge ${assistance.extended_assistance ? 'status-pending' : 'status-delivered'}`}>
                    {assistance.extended_assistance ? 'Requiere' : 'No requiere'}
                  </span>
                </span>
              </div>
            </div>
          </div>
          
          <div className="section">
            <h3>Ubicación</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Dirección:</span>
                <span className="info-value">{assistance.address || 'No registrada'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Referencia:</span>
                <span className="info-value">{assistance.address_reference || 'No registrada'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Geolocalización:</span>
                <span className="info-value">{assistance.geolocation || 'No registrada'}</span>
              </div>
            </div>
          </div>
          
          {assistance.notes && (
            <div className="section">
              <h3>Notas</h3>
              <div className="notes-content">
                {assistance.notes}
              </div>
            </div>
          )}
          
          {assistance.extended_assistance && assistance.extended_assistance_details && (
            <div className="section">
              <h3>Detalles del Seguimiento</h3>
              <div className="notes-content">
                {assistance.extended_assistance_details}
              </div>
            </div>
          )}
          
          <div className="section">
            <h3>Información de Registro</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Fecha de Creación:</span>
                <span className="info-value">{formatDate(assistance.created_at)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Última Actualización:</span>
                <span className="info-value">{formatDate(assistance.updated_at)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssistanceDetail; 